import { createAsyncThunk } from "@reduxjs/toolkit";
import doFetch from "../../infrastructure/doFetch";
import { getQueryParams } from "../../helpers";
import { SPINNER_NAMES } from "../../components/organisms/Spinner/spinner.constants";
import { DEFAULT_LIMIT, EMPTY_STRING } from "../../constants";

const {
  CREATE_COLLECTION,
  UPDATE_COLLECTION,
  CREATE_BACKGROUND_TASK,
  UPDATE_BACKGROUND_TASK,
  UPDATE_RECORD,
  ADD_RECORD,
  CREATE_DATA_MODEL,
  CREATE_ENDPOINT,
  CREATE_SERVICE,
  UPDATE_ENDPOINT,
  UPDATE_SERVICE,
  UPDATE_DATA_MODEL,
  DEPLOY_BE_APP,
  FETCH_SERVICE,
  FETCH_BACKGROUND_TASKS,
  FETCH_COLLECTION,
  FETCH_COLLECTIONS,
  FETCH_DATA_MODELS,
  FETCH_ENDPOINTS,
  FETCH_SERVICES,
  FETCH_MULESOFT_SWAGGER_DATA,
  DELETE_BACKGROUND_TASK,
  DELETE_COLLECTION,
  DELETE_DATA_MODEL,
  DELETE_ENDPOINT,
  DELETE_SERVICE,
  VALIDATE_SQL_EXPRESSION,
  VALIDATE_SMTP_CONNECTION,
  UPDATE_SECRET,
  ADD_SECRET,
  DELETE_SECRET,
  FETCH_SECRETS,
  FETCH_ALL_RECORDS_FROM_TABLE,
  FETCH_DATA_MODEL_DEPLOYMENTS_TRACK,
  EXECUTE_QUERY,
  IMPORT_CSV,
  FETCH_DATA_MODEL,
  ADD_RESOURCES,
} = SPINNER_NAMES;

export const fetchDataModels = createAsyncThunk(
  "fetch-data-models",
  async (filters, args, { useCache, showSpinner } = {}) => {
    const queryParams = filters ? getQueryParams(filters) : "";
    return doFetch({
      url: `dataModels?${queryParams}`,
      method: "GET",
      useCache,
      showSpinner,
      spinner: FETCH_DATA_MODELS,
      errorMessage: "Unable to fetch data models. Please try again later.",
      successMessage: "Successfully fetched data models.",
      ...args,
    });
  }
);

export const updateDataModel = createAsyncThunk(
  "update-data-model",
  async ({ id, dataModel, useCache, showSpinner } = {}, args) =>
    doFetch({
      url: `datamodels/${id}`,
      method: "PATCH",
      config: {
        body: JSON.stringify(dataModel),
      },
      useCache,
      showSpinner,
      spinner: UPDATE_DATA_MODEL,
      errorMessage: "Unable to update data model. Please try again later.",
      successMessage: "Successfully updated data model.",
      ...args,
    })
);
export const fetchDataModel = createAsyncThunk(
  "fetch-data-model",
  async (id, args, { useCache, showSpinner } = {}) =>
    doFetch({
      url: `dataModels/${id}`,
      method: "GET",
      useCache,
      showSpinner,
      spinner: FETCH_DATA_MODEL,
      errorMessage: "Unable to fetch data model. Please try again later.",
      successMessage: "Successfully fetched data model.",
      ...args,
    })
);

export const addNewRecordIntoTable = createAsyncThunk(
  "add-new-record-into-table",
  async ({ id, rowData, useCache, showSpinner } = {}, args) =>
    doFetch({
      url: `data?dataModelId=${id}`,
      method: "POST",
      config: {
        body: JSON.stringify(rowData),
      },
      useCache,
      showSpinner,
      spinner: ADD_RECORD,
      errorMessage: "Unable to create table data. Please try again later.",
      successMessage: "Successfully created table data.",
      ...args,
    })
);

export const updateRecordIntoTable = createAsyncThunk(
  "update-record-into-table",
  async ({ dataModelId, tableDataDetails, useCache, showSpinner } = {}, args) =>
    doFetch({
      url: `data?dataModelId=${dataModelId}`,
      method: "PATCH",
      config: {
        body: JSON.stringify(tableDataDetails),
      },
      useCache,
      showSpinner,
      spinner: UPDATE_RECORD,
      errorMessage: "Unable to update table data. Please try again later.",
      successMessage: "Successfully updated table data.",
      ...args,
    })
);

export const importCSV = createAsyncThunk(
  "import-csv",
  async (
    { dataModelId, updatedTableDetails, useCache, showSpinner } = {},
    args
  ) =>
    doFetch({
      url: `import-csv?dataModelId=${dataModelId}`,
      method: "POST",
      config: {
        body: JSON.stringify(updatedTableDetails),
      },
      useCache,
      showSpinner,
      spinner: IMPORT_CSV,
      errorMessage:
        "Unable to import csv file to update table data. Please try again later.",
      successMessage: "Successfully updated table data.",
      ...args,
    })
);

export const importCollection = createAsyncThunk(
  "import-collections",
  async (
    { dataModelId, updatedTableDetails, useCache, showSpinner } = {},
    args
  ) =>
    doFetch({
      url: `import-collections?dataModelId=${dataModelId}`,
      method: "POST",
      config: {
        body: JSON.stringify(updatedTableDetails),
      },
      useCache,
      showSpinner,
      errorMessage:
        "Unable to import collections from file. Please try again later.",
      successMessage: "Successfully updated table data.",
      ...args,
    })
);

export const fetchAllRecordsFromTable = createAsyncThunk(
  "fetch-all-records-from-table",
  async (
    {
      id,
      tableName,
      filters,
      page,
      limit = DEFAULT_LIMIT,
      useCache,
      showSpinner,
    } = {},
    args
  ) => {
    const queryParams = filters ? getQueryParams(filters) : "";
    const pageCount = page ? page : 1;
    return doFetch({
      url: `data/${id}?tableName=${tableName}&${queryParams}&page=${pageCount}&limit=${limit}`,
      method: "GET",
      useCache,
      showSpinner,
      spinner: FETCH_ALL_RECORDS_FROM_TABLE,
      errorMessage: "Unable to fetch table data. Please try again later.",
      successMessage: "Successfully fetched table data.",
      ...args,
    });
  }
);

export const fetchCollections = createAsyncThunk(
  "fetch-collections",
  async ({ id, filters, useCache, showSpinner } = {}, args) => {
    const queryParams = filters ? getQueryParams(filters) : "";
    return doFetch({
      url: `collections?dataModelId=${id}&${queryParams}`,
      method: "GET",
      useCache,
      showSpinner,
      spinner: FETCH_COLLECTIONS,
      errorMessage: "Unable to fetch collections. Please try again later.",
      successMessage: "Successfully fetched collections.",
      ...args,
    });
  }
);

export const fetchCollection = createAsyncThunk(
  "fetch-collection",
  async (id, args, { useCache, showSpinner } = {}) =>
    doFetch({
      url: `collections/${id}`,
      method: "GET",
      useCache,
      showSpinner,
      spinner: FETCH_COLLECTION,
      errorMessage: "Unable to fetch collection. Please try again later.",
      successMessage: "Successfully fetched collection.",
      ...args,
    })
);

export const createDataModel = createAsyncThunk(
  "create-data-model",
  async (dataModelDetails, args, { useCache, showSpinner } = {}) =>
    doFetch({
      url: `dataModels`,
      method: "POST",
      config: {
        body: JSON.stringify(dataModelDetails),
      },
      useCache,
      showSpinner,
      spinner: CREATE_DATA_MODEL,
      errorMessage: "Unable to create data model. Please try again later.",
      successMessage: "Successfully created data model.",
      ...args,
    })
);

export const deleteDataModel = createAsyncThunk(
  "delete-data-model",
  async (id, args, { useCache, showSpinner } = {}) =>
    doFetch({
      url: `dataModels/${id}`,
      method: "DELETE",
      useCache,
      showSpinner,
      spinner: DELETE_DATA_MODEL,
      errorMessage: "Unable to delete data model. Please try again later.",
      successMessage: "Successfully deleted data model.",
      ...args,
    })
);

export const createCollection = createAsyncThunk(
  "create-collection",
  async (CollectionDetails, args, { useCache, showSpinner } = {}) =>
    doFetch({
      url: `collections?dataModelId=${CollectionDetails.dataModelId}`,
      method: "POST",
      config: {
        body: JSON.stringify(CollectionDetails),
      },
      useCache,
      showSpinner,
      spinner: CREATE_COLLECTION,
      errorMessage: "Unable to create collection. Please try again later.",
      successMessage: "Successfully created collection.",
      ...args,
    })
);

export const deleteCollection = createAsyncThunk(
  "delete-collection",
  async (id, args, { useCache, showSpinner } = {}) =>
    doFetch({
      url: `collections/${id}`,
      method: "DELETE",
      useCache,
      showSpinner,
      spinner: DELETE_COLLECTION,
      errorMessage: "Unable to delete collection. Please try again later.",
      successMessage: "Successfully deleted collection.",
      ...args,
    })
);

export const updateCollection = createAsyncThunk(
  "update-collection",
  async ({ id, data, useCache, showSpinner } = {}, args) =>
    doFetch({
      url: `collections/${id}`,
      method: "PATCH",
      config: {
        body: JSON.stringify(data),
      },
      useCache,
      showSpinner,
      spinner: UPDATE_COLLECTION,
      errorMessage: "Unable to update collection. Please try again later.",
      successMessage: "Successfully updated collection.",
      ...args,
    })
);

export const fetchBackgroundTasks = createAsyncThunk(
  "fetch-background-tasks",
  async ({ id, filters, useCache, showSpinner } = {}, args) => {
    const queryParams = filters ? getQueryParams(filters) : "";
    return doFetch({
      url: `background-tasks?dataModelId=${id}&${queryParams}`,
      method: "GET",
      useCache,
      showSpinner,
      spinner: FETCH_BACKGROUND_TASKS,
      errorMessage: "Unable to fetch background tasks. Please try again later.",
      successMessage: "Successfully fetched background tasks.",
      ...args,
    });
  }
);

export const createBackgroundTask = createAsyncThunk(
  "create-background-task",
  async (data, args, { useCache, showSpinner } = {}) =>
    doFetch({
      url: `background-tasks`,
      method: "POST",
      config: {
        body: JSON.stringify(data),
      },
      useCache,
      showSpinner,
      spinner: CREATE_BACKGROUND_TASK,
      errorMessage: "Unable to create background task. Please try again later.",
      successMessage: "Successfully created background task.",
      ...args,
    })
);

export const deleteBackgroundTask = createAsyncThunk(
  "delete-background-task",
  async (id, args, { useCache, showSpinner } = {}) =>
    doFetch({
      url: `background-tasks/${id}`,
      method: "DELETE",
      useCache,
      showSpinner,
      spinner: DELETE_BACKGROUND_TASK,
      errorMessage: "Unable to delete background task. Please try again later.",
      successMessage: "Successfully deleted background task.",
      ...args,
    })
);

export const fetchEndpoints = createAsyncThunk(
  "fetch-endpoints",
  async ({ id, useCache, showSpinner, filters } = {}, args) => {
    const queryParams = filters ? getQueryParams(filters) : EMPTY_STRING;
    return doFetch({
      url: `endpoints?dataModelId=${id}&${queryParams}`,
      method: "GET",
      useCache,
      showSpinner,
      spinner: FETCH_ENDPOINTS,
      errorMessage: "Unable to fetch endpoints. Please try again later.",
      successMessage: "Successfully fetched endpoints.",
      ...args,
    });
  }
);

export const fetchDataModelRegions = createAsyncThunk(
  "fetch-data-model-regions",
  async ({ useCache, showSpinner } = {}, args) =>
    doFetch({
      url: "library?type=dataModelRegions",
      method: "GET",
      useCache,
      showSpinner,
      errorMessage:
        "Unable to fetch data model regions. Please try again later.",
      successMessage: "Successfully fetched data model regions.",
      ...args,
    })
);

export const createEndpoint = createAsyncThunk(
  "create-endpoint",
  async (data, args, { useCache, showSpinner } = {}) =>
    doFetch({
      url: `endpoints`,
      method: "POST",
      config: {
        body: JSON.stringify(data),
      },
      useCache,
      showSpinner,
      spinner: CREATE_ENDPOINT,
      errorMessage: "Unable to create endpoint. Please try again later.",
      successMessage: "Successfully created endpoint.",
      ...args,
    })
);

export const updateEndpoint = createAsyncThunk(
  "update-endpoint",
  async ({ id, data, useCache, showSpinner } = {}, args) =>
    doFetch({
      url: `endpoints/${id}`,
      method: "PATCH",
      config: {
        body: JSON.stringify(data),
      },
      useCache,
      showSpinner,
      spinner: UPDATE_ENDPOINT,
      errorMessage: "Unable to update endpoint. Please try again later.",
      successMessage: "Successfully created endpoint.",
      ...args,
    })
);

export const deleteEndpoint = createAsyncThunk(
  "delete-endpoint",
  async (id, args, { useCache, showSpinner } = {}) =>
    doFetch({
      url: `endpoints/${id}`,
      method: "DELETE",
      useCache,
      showSpinner,
      spinner: DELETE_ENDPOINT,
      errorMessage: "Unable to delete endpoint. Please try again later.",
      successMessage: "Successfully deleted endpoint.",
      ...args,
    })
);

export const updateBackgroundTask = createAsyncThunk(
  "update-background-task",
  async ({ id, backgroundTaskDetails, useCache, showSpinner } = {}, args) =>
    doFetch({
      url: `background-tasks/${id}`,
      method: "PATCH",
      config: {
        body: JSON.stringify(backgroundTaskDetails),
      },
      useCache,
      showSpinner,
      spinner: UPDATE_BACKGROUND_TASK,
      errorMessage: "Unable to update background task. Please try again later.",
      successMessage: "Successfully updated background task.",
      ...args,
    })
);

export const createService = createAsyncThunk(
  "create-service",
  async (data, args, { useCache, showSpinner } = {}) =>
    doFetch({
      url: `services`,
      method: "POST",
      config: {
        body: JSON.stringify(data),
      },
      useCache,
      showSpinner,
      spinner: CREATE_SERVICE,
      errorMessage: "Unable to create service. Please try again later.",
      successMessage: "Successfully created service.",
      ...args,
    })
);

export const updateService = createAsyncThunk(
  "update-service",
  async ({ id, data, useCache, showSpinner } = {}, args) =>
    doFetch({
      url: `services/${id}`,
      method: "PATCH",
      config: {
        body: JSON.stringify(data),
      },
      useCache,
      showSpinner,
      spinner: UPDATE_SERVICE,
      errorMessage: "Unable to update service. Please try again later.",
      successMessage: "Successfully updated service.",
      ...args,
    })
);

export const fetchServices = createAsyncThunk(
  "fetch-services",
  async ({ id, filters, useCache, showSpinner } = {}, args) => {
    const queryFilters = filters
      ? { ...filters, dataModelId: id }
      : { dataModelId: id };
    const queryParams = getQueryParams(queryFilters);
    return doFetch({
      url: `services?${queryParams}`,
      method: "GET",
      useCache,
      showSpinner,
      spinner: FETCH_SERVICES,
      errorMessage: "Unable to fetch services. Please try again later.",
      successMessage: "Successfully fetched services.",
      ...args,
    });
  }
);

export const fetchService = createAsyncThunk(
  "fetch-service",
  async (id, args, { useCache, showSpinner } = {}) =>
    doFetch({
      url: `services/${id}`,
      method: "GET",
      useCache,
      showSpinner,
      spinner: FETCH_SERVICE,
      errorMessage: "Unable to fetch service. Please try again later.",
      successMessage: "Successfully fetched service.",
      ...args,
    })
);

export const deleteService = createAsyncThunk(
  "delete-service",
  async (id, args, { useCache, showSpinner } = {}) =>
    doFetch({
      url: `services/${id}`,
      method: "DELETE",
      useCache,
      showSpinner,
      spinner: DELETE_SERVICE,
      errorMessage: "Unable to delete service. Please try again later.",
      successMessage: "Successfully deleted service.",
      ...args,
    })
);

export const fetchSwaggerData = createAsyncThunk(
  "fetch-swagger-data",
  async ({ id, useCache, showSpinner } = {}, args) =>
    doFetch({
      url: `swagger?dataModelId=${id}`,
      method: "GET",
      useCache,
      showSpinner,
      spinner: FETCH_MULESOFT_SWAGGER_DATA,
      errorMessage: "Unable to fetch Swagger Data. Please try again later.",
      successMessage: "Successfully fetched Swagger Data.",
      ...args,
    })
);

export const deployBEApp = createAsyncThunk(
  "deploy-BE-app",
  async (data, args, { useCache, showSpinner } = {}) =>
    doFetch({
      url: `deploy-app`,
      method: "POST",
      config: {
        body: JSON.stringify(data),
      },
      useCache,
      showSpinner,
      spinner: DEPLOY_BE_APP,
      errorMessage: "Unable to create service. Please try again later.",
      successMessage: "Successfully created service.",
      ...args,
    })
);

export const validateSqlExpression = createAsyncThunk(
  "validate-sql-expression",
  async (data, args, { useCache, showSpinner } = {}) =>
    doFetch({
      url: `validate-sql-expression`,
      method: "POST",
      config: {
        body: JSON.stringify(data),
      },
      useCache,
      showSpinner,
      spinner: VALIDATE_SQL_EXPRESSION,
      errorMessage: "Invalid SQL Expression.",
      successMessage: "Successfully validated SQL expression.",
      ...args,
    })
);

export const validateSmtpConnection = createAsyncThunk(
  "validate-smtp-connection",
  async (data, args, { useCache, showSpinner } = {}) =>
    doFetch({
      url: `validate-smtp-connection`,
      method: "POST",
      config: {
        body: JSON.stringify(data),
      },
      useCache,
      showSpinner,
      spinner: VALIDATE_SMTP_CONNECTION,
      errorMessage: "Invalid SMTP connection.",
      successMessage: "Successfully validated SMTP connection.",
      ...args,
    })
);

export const fetchPipelineStatus = createAsyncThunk(
  "fetch-backend-deploy-status",
  async (filters, args, { useCache, showSpinner } = {}) => {
    const queryParams = filters ? getQueryParams(filters) : "";
    return doFetch({
      url: `deploy-status?${queryParams}`,
      method: "GET",
      useCache,
      showSpinner,
      spinner: DEPLOY_BE_APP,
      errorMessage: "Unable to fetch pipeline status. Please try again later.",
      successMessage: "Successfully fetched pipeline status.",
      ...args,
    });
  }
);

export const fetchReferenceCollectionsData = createAsyncThunk(
  "fetch-reference-collections-data",
  async ({ id, tableName, useCache, showSpinner }, args) => {
    return doFetch({
      url: `data/${id}?tableName=${tableName}`,
      method: "GET",
      useCache,
      showSpinner,
      errorMessage:
        "Unable to fetch reference table data. Please try again later.",
      successMessage: "Successfully fetched reference table data.",
      ...args,
    });
  }
);

export const fetchENVSecrets = createAsyncThunk(
  "fetch-env-secrets",
  async (id, args, { useCache, showSpinner } = {}) =>
    doFetch({
      url: `env-config?dataModelId=${id}`,
      method: "GET",
      useCache,
      showSpinner,
      spinner: FETCH_SECRETS,
      errorMessage: "Unable to fetch secrets. Please try again later.",
      successMessage: "Successfully fetched secrets.",
      ...args,
    })
);
export const createSecret = createAsyncThunk(
  "create-secrets",
  async ({ id, data, useCache, showSpinner } = {}, args) =>
    doFetch({
      url: `env-config?dataModelId=${id}`,
      method: "POST",
      config: {
        body: JSON.stringify(data),
      },
      useCache,
      showSpinner,
      spinner: ADD_SECRET,
      errorMessage: "Unable to create secret. Please try again later.",
      successMessage: "Successfully created secret.",
      ...args,
    })
);

export const deleteSecret = createAsyncThunk(
  "delete-secret-key",
  async (id, args, { useCache, showSpinner } = {}) =>
    doFetch({
      url: `env-config/${id}`,
      method: "DELETE",
      useCache,
      showSpinner,
      spinner: DELETE_SECRET,
      errorMessage: "Unable to delete secret key. Please try again later.",
      successMessage: "Successfully deleted secret key.",
      ...args,
    })
);

export const updateSecretKey = createAsyncThunk(
  "update-secret-key",
  async ({ id, data, useCache, showSpinner } = {}, args) =>
    doFetch({
      url: `env-config/${id}`,
      method: "PATCH",
      config: {
        body: JSON.stringify(data),
      },
      useCache,
      showSpinner,
      spinner: UPDATE_SECRET,
      errorMessage: "Unable to update secret key. Please try again later.",
      successMessage: "Successfully updated secret key.",
      ...args,
    })
);

export const fetchDataModelDeploymentsTrack = createAsyncThunk(
  "fetch-data-model-deployments",
  async (id, args, { useCache, showSpinner } = {}) =>
    doFetch({
      url: `deployments-status?dataModelId=${id}`,
      method: "GET",
      useCache,
      showSpinner,
      spinner: FETCH_DATA_MODEL_DEPLOYMENTS_TRACK,
      errorMessage:
        "Unable to get Backend Deployments Track. Please try again later.",
      successMessage: "Successfully fetched Backend Deployments Track.",
      ...args,
    })
);

export const executeQuery = createAsyncThunk(
  "queryTool",
  async (data, args, { useCache, showSpinner } = {}) =>
    doFetch({
      url: `queryTool`,
      method: "POST",
      config: {
        body: JSON.stringify(data),
      },
      useCache,
      showSpinner,
      spinner: EXECUTE_QUERY,
      errorMessage: "SQL expression not executed !",
      successMessage: "SQL expression executed successfully !",
      ...args,
    })
);

export const fetchFilteredDataModels = createAsyncThunk(
  "fetch-filtered-data-models",
  async (filters, args, { useCache, showSpinner } = {}) => {
    const queryParams = filters ? getQueryParams(filters) : "";
    return doFetch({
      url: `dataModels?${queryParams}`,
      method: "GET",
      useCache,
      showSpinner,
      errorMessage:
        "Unable to fetch filtered data models. Please try again later.",
      successMessage: "Successfully fetched filtered data models.",
      ...args,
    });
  }
);

export const createCollectionFromTemplates = createAsyncThunk(
  "create-collection-through-datamodel-or-template",
  async (data, args, { useCache, showSpinner } = {}) =>
    doFetch({
      url: `create-datamodel-resources`,
      method: "POST",
      config: {
        body: JSON.stringify(data),
      },
      useCache,
      showSpinner,
      spinner: ADD_RESOURCES,
      errorMessage: "Unable to add resources. Please try again later.",
      successMessage: "Successfully added resources.",
      ...args,
    })
);
